import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import GamificationService from '../services/GamificationService';
import DisCard from '../components/design-system/DisCard';
import FloatingMenu from '../components/FloatingMenu';

const BADGE_ICONS = {
  star: '\u2B50',
  microscope: '\uD83D\uDD2C',
  book_open: '\uD83D\uDCDA',
  brain: '\uD83E\uDDE0',
  book: '\uD83D\uDCD6',
  flame: '\uD83D\uDD25',
  trophy: '\uD83C\uDFC6',
};

export default function BadgesPage() {
  const { childId } = useParams();
  const { api } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      if (!api || !childId) return;
      const svc = GamificationService.getInstance();
      svc.setApi(api);
      const result = await svc.getBadges(childId);
      setData(result);
      setLoading(false);
    };
    load();
  }, [api, childId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#f0f7f4] to-white flex items-center justify-center">
        <div className="animate-pulse text-teal-600">Chargement...</div>
      </div>
    );
  }

  const unlocked = data?.badges?.filter(b => b.unlocked) || [];
  const locked = data?.badges?.filter(b => !b.unlocked) || [];

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#f0f7f4] to-white pb-24" data-testid="badges-page">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#4A90A4] to-[#6BB5A2] p-6 pb-8">
        <button
          onClick={() => navigate(-1)}
          className="text-white/80 text-sm mb-2 hover:text-white"
          data-testid="badges-back-btn"
        >
          &larr; Retour
        </button>
        <h1
          className="text-2xl font-bold text-white"
          style={{ fontFamily: "'Caveat', cursive" }}
        >
          Récompenses de {data?.child_name || '...'}
        </h1>
        <p className="text-white/70 text-sm mt-1">
          {unlocked.length} / {data?.badges?.length || 0} badges débloqués
        </p>
      </div>

      <div className="px-4 -mt-4 space-y-4">
        {/* Unlocked badges */}
        {unlocked.length > 0 && (
          <div>
            <h2 className="text-sm font-semibold text-gray-500 mb-2 mt-4">Badges obtenus</h2>
            <div className="grid grid-cols-2 gap-3">
              {unlocked.map(badge => (
                <DisCard
                  key={badge.id}
                  className="p-4 text-center bg-gradient-to-br from-amber-50 to-yellow-50 border-amber-200"
                  data-testid={`badge-${badge.id}`}
                >
                  <div className="text-3xl mb-2">{BADGE_ICONS[badge.icon] || '\u2B50'}</div>
                  <p className="text-sm font-bold text-amber-800">{badge.name}</p>
                  <p className="text-[10px] text-amber-500 mt-1">{badge.description}</p>
                  {navigator.share && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        navigator.share({
                          title: `Badge : ${badge.name}`,
                          text: `${data?.child_name} vient de gagner le badge "${badge.name}" sur Dis Maman ! \u2B50`,
                          url: 'https://dismaman.com',
                        }).catch(() => {});
                      }}
                      className="mt-2 text-[10px] text-amber-400 underline"
                      data-testid={`badge-share-${badge.id}`}
                    >
                      Partager
                    </button>
                  )}
                </DisCard>
              ))}
            </div>
          </div>
        )}

        {/* Locked badges */}
        {locked.length > 0 && (
          <div>
            <h2 className="text-sm font-semibold text-gray-500 mb-2 mt-4">Badges à débloquer</h2>
            <div className="grid grid-cols-2 gap-3">
              {locked.map(badge => (
                <DisCard
                  key={badge.id}
                  className="p-4 text-center opacity-60"
                  data-testid={`badge-locked-${badge.id}`}
                >
                  <div className="text-3xl mb-2 grayscale">{BADGE_ICONS[badge.icon] || '\u2B50'}</div>
                  <p className="text-sm font-bold text-gray-600">{badge.name}</p>
                  {badge.progress ? (
                    <div className="mt-2">
                      <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                        <div
                          className="bg-gradient-to-r from-amber-300 to-amber-400 h-2 rounded-full transition-all duration-500"
                          style={{ width: `${Math.min(100, (badge.progress.current / badge.progress.target) * 100)}%` }}
                        />
                      </div>
                      <p className="text-[10px] text-gray-500 mt-1 font-medium">
                        {badge.progress.current}/{badge.progress.target}
                        {badge.progress.target - badge.progress.current > 0
                          ? ` — encore ${badge.progress.target - badge.progress.current} !`
                          : ''
                        }
                      </p>
                    </div>
                  ) : (
                    <p className="text-[10px] text-gray-400 mt-1">{badge.condition_text}</p>
                  )}
                </DisCard>
              ))}
            </div>
          </div>
        )}
      </div>

      <FloatingMenu />
    </div>
  );
}
